import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { ErrorMessage, Field, FieldProps, Form, Formik, FormikActions } from 'formik'
import { FiCheckSquare, FiHelpCircle, FiSquare } from 'react-icons/fi'
// @ts-ignore
import { TransactionReceipt } from 'web3-core'
import { useToasts } from 'react-toast-notifications'
import { Tooltip } from 'react-lightweight-tooltip'
import { getDateString } from '../../lib/helpers/dateHelper'

/* Helpers */
import {
  getClaimHash,
  getTransactionRequest,
  HashClaim,
  postClaimHash,
  postTokenFreeze,
  postTokenMigration,
  TransactionRequestStatus,
  TransactionStatus,
} from 'api'
import { AddressSchema } from 'lib/schemas'
import { useInterval } from 'lib/hooks/useInterval'

/* Components */
import ClaimFooterMessage from './ClaimFooterMessage'
import { SubmitButton } from 'components/SubmitButton'
import { TxDetail } from 'components/TxDetail'

/* Lib */
import { isValidEmail } from 'lib/helpers'
import { useImageSrc } from 'lib/hooks/useImageSrc'
import { COLORS, STYLES } from 'lib/constants'

/* ABI */
import abi from 'abis/PoapDelegatedMint.json'
import { parse } from 'date-fns'
import { analyticsTracking } from 'lib/analytics/analytics'
import { usePrivyWrapper } from '../../hooks/usePrivyWrapper'
import Web3 from 'web3'
import { NETWORK } from '../../poap-eth'

type QRFormValues = {
  address: string
}

interface MigrationParams {
  signature: string
  expirationTime: number
}

const CONTRACT_ADDRESS = process.env.REACT_APP_MINT_DELEGATE_CONTRACT

const ClaimForm: React.FC<{
  claim?: HashClaim
  address?: string
  onSubmit: (claim: HashClaim) => void
}> = ({ claim, address, onSubmit }) => {
  const [claimed, setClaimed] = useState<boolean>(false)
  const [account, setAccount] = useState<string>(address || '')
  const [migrateInProcess, setMigrateInProcess] = useState<boolean>(false)
  const [migrate, setMigrate] = useState<boolean>(false)
  const [token, setToken] = useState<number | null>(null)
  const [web3, setWeb3] = useState<any>(null)
  const [completeClaim, setCompleteClaim] = useState<HashClaim | null>(null)
  const [txHash, setTxHash] = useState<string>('')
  const [txReceipt, setTxReceipt] = useState<null | TransactionReceipt>(null)
  const [transactionRequestId, setTransactionRequestId] = useState<number | undefined>(undefined)
  const [isFrozen, setIsFrozen] = useState<boolean>(false)
  const [migrationParams, setMigrationParams] = useState<MigrationParams | null>(null)
  const { connectWallet: connectWalletWithPrivy, lastConnectedWallet, recentConnectedWallet } = usePrivyWrapper()

  const mobileImageUrlRaw = undefined
  const mobileImageLink = undefined
  const mainColor = undefined
  const mobileImageUrl = useImageSrc(mobileImageUrlRaw)
  const isNoCoinerFlowEnabled: boolean = !!claim?.event.minting_config?.available_wallet_providers?.length

  const { addToast } = useToasts()

  const termsOfServiceURL = 'https://poap.xyz/terms-of-service'
  const privacyPolicyURL = 'https://poap.xyz/privacy-policy'

  useEffect(() => {
    if (migrateInProcess && !token) {
      const interval = setInterval(fetchClaim, 5000)
      return () => clearInterval(interval)
    }
  }, [migrateInProcess, token]) /* eslint-disable-line react-hooks/exhaustive-deps */

  useEffect(() => {
    startMigration()
  }, [token]) /* eslint-disable-line react-hooks/exhaustive-deps */

  useEffect(() => {
    if (txHash && web3) {
      const interval = setInterval(() => {
        getReceipt()
      }, 3000)
      return () => clearInterval(interval)
    }
  }, [txHash, txReceipt]) /* eslint-disable-line react-hooks/exhaustive-deps */

  useEffect(() => {
    if (recentConnectedWallet && migrateInProcess) {
      recentConnectedWallet.getWeb3jsProvider().then((provider) => {
        const _web3 = new Web3(provider)
        _web3.eth.net.getNetworkType().then((connectedNetwork) => {
          const networkError = connectedNetwork && NETWORK.toLowerCase().indexOf(connectedNetwork.toLowerCase()) === -1
          if (networkError) {
            let message = `Wrong network, please connect to ${NETWORK}.`
            addToast(message, {
              appearance: 'error',
              autoDismiss: false,
            })
            setIsFrozen(false)
            setTransactionRequestId(undefined)
            setMigrateInProcess(false)
          } else {
            setWeb3(_web3)
          }
        })
      })
    }
  }, [recentConnectedWallet])

  useEffect(() => {
    if (lastConnectedWallet) {
      setAccount(lastConnectedWallet.address)
    }
  }, [lastConnectedWallet])

  const startMigration = () => {
    if (token) {
      postTokenFreeze(token)
        .then((result) => {
          if (result) {
            setTransactionRequestId(result.transactionRequestId)
          }
        })
        .catch((err) => {
          setIsFrozen(true)
        })
    }
  }

  useEffect(() => {
    if (isFrozen && token) {
      setTimeout(function () {
        postTokenMigration(token)
          .then((result) => {
            if (result) {
              connectWalletForMigration(result.signature, result.expirationTime)
            }
          })
          .catch((err) => {
            setIsFrozen(false)
            setTransactionRequestId(undefined)
            setMigrateInProcess(false)
            let message = `Error while trying to submit transaction.\nPlease try again in a few minutes`
            addToast(message, {
              appearance: 'error',
              autoDismiss: false,
            })
          })
      }, 2000)
    }
  }, [isFrozen]) /* eslint-disable-line react-hooks/exhaustive-deps */

  useInterval(() => {
    if (transactionRequestId && !isFrozen && migrateInProcess) {
      const fetchTransactionRequest = async () => {
        const transactionRequest = await getTransactionRequest(transactionRequestId)
        if (transactionRequest.status === TransactionRequestStatus.finish && transactionRequest.result) {
          setIsFrozen(true)
        }
        if (transactionRequest.status === TransactionRequestStatus.finish_with_error) {
          showErrorMessage()
        }
      }
      fetchTransactionRequest()
    }
  }, 3000)

  const connectWalletForMigration = (signature: string, expirationTime: number) => {
    setMigrationParams({ signature, expirationTime })
    if (!recentConnectedWallet) {
      connectWalletWithPrivy()
    }
  }

  const migrateToken = useCallback(
    async (signature: string, expirationTime: number) => {
      if (!web3) return null
      const accounts = await web3.eth.getAccounts()
      if (accounts.length === 0) return null

      const account = accounts[0]

      if (!completeClaim) return
      const { event, beneficiary } = completeClaim

      try {
        const contract = new web3.eth.Contract(abi, CONTRACT_ADDRESS)
        let fee = await contract.methods.migrationFee().call()
        let gas = 1000000
        try {
          gas = await contract.methods
            .mintToken(event.id, token, beneficiary, expirationTime, signature)
            .estimateGas({ from: account })
          gas = Math.floor(gas * 1.3)
        } catch (e) {
          gas = Math.floor(gas * 1.3)
        }

        contract.methods
          .mintToken(event.id, token, beneficiary, expirationTime, signature)
          .send({ from: account, gas: gas, value: fee }, (err: any, hash: string | null) => {
            if (err) {
              setIsFrozen(false)
              setTransactionRequestId(undefined)
              setMigrateInProcess(false)
              showErrorMessage()
            }
            if (hash) {
              setTxHash(hash)
            }
          })
      } catch (e) {
        setIsFrozen(false)
        setTransactionRequestId(undefined)
        setMigrateInProcess(false)
        showErrorMessage()
      }
    },
    [web3, token] /* eslint-disable-line react-hooks/exhaustive-deps */,
  )

  useEffect(() => {
    if (web3 && migrationParams) {
      migrateToken(migrationParams.signature, migrationParams.expirationTime)
    }
  }, [migrationParams, web3, migrateToken])

  const getReceipt = async () => {
    let receipt: null | TransactionReceipt = null
    if (web3 && txHash !== '' && !txReceipt) {
      receipt = await web3.eth.getTransactionReceipt(txHash)
      if (receipt) {
        setTimeout(() => setTxReceipt(receipt), 1000)
      }
    }

    if (!receipt || !receipt.status || (txReceipt && !txReceipt.status)) {
      setMigrateInProcess(false)
    }

    if (receipt && receipt.status && completeClaim) onSubmit(completeClaim)
  }

  const toggleCheckbox = () => setMigrate(!migrate)

  const handleFormSubmit = async (values: QRFormValues, actions: FormikActions<QRFormValues>) => {
    if (claimed) {
      startMigration()
      return
    }

    actions.setSubmitting(true)
    try {
      if (claim) {
        analyticsTracking.claim.trackRequestTransaction(claim)
        const newClaim = await postClaimHash(
          claim.qr_hash.toLowerCase(),
          values.address.toLowerCase().trim(),
          claim.secret,
          claim.event.minting_config?.available_wallet_providers?.[0],
        )
        analyticsTracking.claim.trackAcceptedTransaction(newClaim)
        setClaimed(true)
        if (migrate && !isValidEmail(values.address)) {
          setMigrateInProcess(true)
          setCompleteClaim(newClaim)
          actions.setSubmitting(false)
        } else {
          onSubmit(newClaim)
        }
      }
    } catch (error) {
      analyticsTracking.claim.trackUnsuccessfulRequestTransaction(error, claim)
      // @ts-expect-error TODO: error is type unknown
      if (error.message) {
        actions.setStatus({
          ok: false,
          // @ts-expect-error TODO: error is type unknown
          msg: error.message.includes('timeout-or-duplicate')
            ? 'Reload page and try again'
            : // @ts-expect-error TODO: error is type unknown
              `Badge couldn't be claimed: ${error.message}`,
        })
      }
      actions.setSubmitting(false)
    }
  }

  const fetchClaim = async () => {
    if (!claim) return
    getClaimHash(claim.qr_hash.toLowerCase()).then((claim) => {
      setCompleteClaim(claim)
      if (claim && claim.tx_status === TransactionStatus.PASSED && claim.result && claim.result.token) {
        setToken(claim.result.token)
      }
    })
  }

  const showErrorMessage = () => {
    setMigrateInProcess(false)
    let message = `Error while trying to submit transaction.\nPlease try again.`
    addToast(message, {
      appearance: 'error',
      autoDismiss: false,
    })
  }

  let CheckboxIcon = !migrate ? FiCheckSquare : FiSquare

  const migrationText = [
    <div key="migrationText" className={'backoffice-tooltip'}>
      All POAPs are minted in xDAI, but should you want your POAP in mainnet, un-check this checkbox so that you can
      submit the transaction to migrate the badge to mainnet. You'll need to pay for the transaction cost. Not available
      for claims with email.
    </div>,
  ]
  const eventDate = claim && parse(claim.event.expiry_date, 'dd-MMM-yyyy', new Date())
  if (eventDate && eventDate < new Date()) {
    return (
      <div className={'container claim-info'} data-aos="fade-up" data-aos-delay="300">
        This POAP can’t be minted because it’s been too long since the event finished
        <br />
        If you think this is a mistake, try using Chrome or Safari
      </div>
    )
  }

  return (
    <div className={'container claim-info'} data-aos="fade-up" data-aos-delay="300">
      <div>
        {isNoCoinerFlowEnabled && (
          <div className="no-wallet-no-problem">
            <span className="question-text">No wallet? No problem</span>
            <span>
              If you use an email address to mint this POAP, we will create a new wallet with Privy that can be claimed
              later.
            </span>
          </div>
        )}
        <Formik
          enableReinitialize
          onSubmit={handleFormSubmit}
          initialValues={{ address: account }}
          isInitialValid={account !== ''}
          validationSchema={AddressSchema}
        >
          {({ isValid, isSubmitting, handleChange, status }) => {
            return (
              <Form className="claim-form">
                <Field
                  name="address"
                  render={({ field, form }: FieldProps) => {
                    return (
                      <input
                        type="text"
                        autoComplete="off"
                        style={{ borderColor: mainColor ?? COLORS.primaryColor }}
                        className={classNames(!!form.errors[field.name] && 'error')}
                        placeholder="Enter your email, ENS or Ethereum address"
                        {...field}
                        disabled={claimed}
                        onChange={(e) => {
                          handleChange(e)
                          setAccount(e.target.value)
                        }}
                      />
                    )
                  }}
                />
                <ErrorMessage name="gasPrice" component="p" className="bk-error" />
                {status && <p className={status.ok ? 'bk-msg-ok' : 'bk-msg-error'}>{status.msg}</p>}
                {!txHash && (
                  <>
                    <div className={'address-link'}>
                      <div>
                        <span onClick={connectWalletWithPrivy}>Get my address</span>
                      </div>
                    </div>
                    <div
                      className={'layer-checkbox'}
                      onClick={!isSubmitting && !migrateInProcess && !claimed ? toggleCheckbox : () => {}}
                    >
                      <CheckboxIcon color={mainColor ?? COLORS.primaryColor} /> Free minting in Gnosis{' '}
                      {/* @ts-ignore TODO: remove ignore TS validation */}
                      <Tooltip content={migrationText}>
                        <FiHelpCircle color={mainColor ?? COLORS.primaryColor} />
                      </Tooltip>
                    </div>
                    <p className={'terms-block'}>
                      By minting or reserving your POAP, you accept POAP Inc’s
                      <a href={termsOfServiceURL} target="_blank">
                        {` Terms of Service `}
                      </a>
                      and
                      <a href={privacyPolicyURL} target="_blank">
                        {` Privacy Policy.`}
                      </a>
                    </p>
                    <SubmitButton
                      text="Mint POAP"
                      className="mint-button"
                      style={{
                        backgroundColor: mainColor ?? COLORS.primaryColor,
                        boxShadow: mainColor ? STYLES.boxShadow(mainColor) : '',
                      }}
                      isSubmitting={isSubmitting || migrateInProcess}
                      canSubmit={isValid}
                    />
                  </>
                )}
              </Form>
            )
          }}
        </Formik>
      </div>

      {txHash && <TxDetail hash={txHash} status={'passed'} chain_id={1} />}

      {txReceipt && !txReceipt.status && (
        <div className={'text-info'}>
          <p>It seems that your transaction failed. Please refresh the page</p>
        </div>
      )}

      <ClaimFooterMessage
        linkStyle={{ color: mainColor ?? COLORS.primaryColor }}
        expiryDate={claim && getDateString(new Date(claim.event.expiry_date))}
      />
      <div className="mobile-image">
        {mobileImageUrl ? (
          mobileImageLink ? (
            <a href={mobileImageLink} rel="noopener noreferrer" target="_blank">
              <img alt="Brand publicity" src={mobileImageUrl} />
            </a>
          ) : (
            <img alt="Brand publicity" src={mobileImageUrl} />
          )
        ) : null}
      </div>
    </div>
  )
}

export default ClaimForm
